import { useEffect, useState } from 'react';
import useAppStore from '@/stores/appStore';
import api, { BACKEND_URL } from '@/lib/api';

// Helper to convert hex color to HSL values (channel numbers)
const hexToHsl = (hex) => {
  if (!hex || typeof hex !== 'string') return '217 100% 33%';
  let c = hex.replace('#', '').trim();
  if (c.length === 3) {
    c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  }
  if (c.length !== 6) return '217 100% 33%';
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const useTenant = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const setTenantConfig = useAppStore((state) => state.setTenantConfig);
  const [loading, setLoading] = useState(!tenantConfig);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tenantConfig) {
      setLoading(false);
      return;
    }

    const fetchTenantConfig = async () => {
      try {
        // Backend resolves the tenant from the request Host header (or X-Tenant on
        // local dev) via TenantContextMiddleware — no slug guessing needed here.
        const { data } = await api.get('/tenants/me');
        
        // Resolve absolute URL for logo if relative
        if (data.logo_url && !data.logo_url.startsWith('http://') && !data.logo_url.startsWith('https://')) {
          data.logo_url = `${BACKEND_URL}${data.logo_url}`;
        }
        
        setTenantConfig(data);
        
        // Inject CSS Custom Properties for tenant branding
        const root = document.documentElement;
        const primaryHsl = hexToHsl(data.brand_colour || '#002FA7');
        root.style.setProperty('--primary', primaryHsl);
        root.style.setProperty('--ring', primaryHsl);
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to load tenant configuration:', err);
        setError(err);
        setLoading(false);
      }
    };

    fetchTenantConfig();
  }, [tenantConfig, setTenantConfig]);

  return { tenantConfig, loading, error };
};
